import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_TITLE,
  INDEXABLE_PATHS,
  SITE_URL,
  getPageMeta,
  normalizePath,
} from "./pageMeta";
import { absoluteShareImage } from "./sharePreview";

export { SITE_URL } from "./pageMeta";

const PRODUCTION_HOSTS = ["awasource.com", "www.awasource.com"];

const publicUrl = process.env.PUBLIC_URL || "";

// Browsers request /favicon.ico on their own, and some only honour icons
// declared in the live <head>. Emit the full set on every route.
const FAVICON_LINKS = [
  { rel: "icon", href: `${publicUrl}/favicon.ico`, sizes: "any" },
  { rel: "icon", href: `${publicUrl}/favicon-32.png`, type: "image/png", sizes: "32x32" },
  { rel: "icon", href: `${publicUrl}/favicon-16.png`, type: "image/png", sizes: "16x16" },
  { rel: "apple-touch-icon", href: `${publicUrl}/apple-touch-icon.png`, sizes: "180x180" },
];

const plain = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const clip = (value, max) => {
  const text = plain(value);
  if (!text || text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  const base = space > Math.floor(max * 0.6) ? cut.slice(0, space) : cut;
  return `${base.trim()}…`;
};

// Pages already pass titles like "Awasource | Login" or "Features | Awasource".
// titleTemplate adds the brand once, so strip it here to avoid doubling it.
const cleanTitle = (title) =>
  plain(title)
    .replace(/^awasource\s*[|—–-]\s*/i, "")
    .replace(/\s*[|—–-]\s*awasource$/i, "");

const breadcrumbLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((crumb, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: crumb.name,
    item: `${SITE_URL}${crumb.path === "/" ? "/" : crumb.path}`,
  })),
});

const DocumentHeader = ({
  title,
  description = "",
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  noindex,
  jsonLd,
}) => {
  const { pathname } = useLocation();
  const routeMeta = getPageMeta(pathname);
  const isProduction = PRODUCTION_HOSTS.includes(window.location.hostname);
  const isIndexablePath = INDEXABLE_PATHS.some((re) => re.test(pathname));
  const allowIndex = isProduction && isIndexablePath && !noindex;

  const pageTitle = clip(cleanTitle(title || routeMeta?.title || ""), 55);
  const fullTitle = pageTitle ? `${pageTitle} | Awasource` : DEFAULT_TITLE;
  const metaDescription = clip(
    description || routeMeta?.description || DEFAULT_DESCRIPTION,
    160
  );

  // Canonical = production host + path only (drops campaign parameters and
  // collapses www, staging, and trailing slashes onto one URL).
  const canonicalPath = normalizePath(pathname);
  const canonicalUrl = `${SITE_URL}${canonicalPath === "/" ? "/" : canonicalPath}`;

  const shareTitle = ogTitle || fullTitle;
  const shareDescription = clip(ogDescription || metaDescription, 200);
  const shareUrl = ogUrl || canonicalUrl;
  const shareImage = ogImage || absoluteShareImage(canonicalPath);

  const blocks = [];
  if (routeMeta?.jsonLd) blocks.push(routeMeta.jsonLd);
  if (jsonLd) blocks.push(...(Array.isArray(jsonLd) ? jsonLd : [jsonLd]));
  if (routeMeta?.crumbs?.length > 1) blocks.push(breadcrumbLd(routeMeta.crumbs));

  return (
    <Helmet
      titleTemplate="%s | Awasource"
      defaultTitle={isIndexablePath ? DEFAULT_TITLE : "Awasource"}
      defer={false}
    >
      {pageTitle ? <title>{pageTitle}</title> : null}
      <meta name="description" content={metaDescription} />
      <meta
        name="robots"
        content={
          allowIndex
            ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
            : "noindex, nofollow, noarchive, nosnippet"
        }
      />
      <link rel="canonical" href={canonicalUrl} />
      {FAVICON_LINKS.map((icon) => (
        <link key={icon.href} {...icon} />
      ))}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Awasource" />
      <meta property="og:locale" content="en_NG" />
      <meta property="og:url" content={shareUrl} />
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={shareDescription} />
      <meta property="og:image" content={shareImage} />
      <meta property="og:image:secure_url" content={shareImage} />
      <meta property="og:image:type" content="image/jpeg" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={shareTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@awasourcestan" />
      <meta name="twitter:url" content={shareUrl} />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={shareDescription} />
      <meta name="twitter:image" content={shareImage} />

      {blocks.map((block) => (
        <script key={block["@type"]} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default DocumentHeader;
